import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { LogOut, Mail, Phone, Trash2, RefreshCw, Loader2, Inbox, FileText, Image as ImageIcon, LayoutGrid, Settings, ShieldCheck, Files } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import CMSEditor from "@/pages/admin/CMSEditor";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import GalleryAdmin from "@/pages/admin/GalleryAdmin";
import SettingsAdmin from "@/pages/admin/SettingsAdmin";
import ChangePassword from "@/pages/admin/ChangePassword";
import PagesAdmin from "@/pages/admin/PagesAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [tab, setTab] = useState("requests");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/quote-requests`, {
                withCredentials: true,
            });
            setItems(data);
        } catch (e) {
            toast.error("Impossible de charger les demandes.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (tab === "requests") load();
    }, [load, tab]);

    const remove = async (id) => {
        if (!window.confirm("Supprimer définitivement cette demande ?")) return;
        setDeletingId(id);
        try {
            await axios.delete(`${API}/admin/quote-requests/${id}`, {
                withCredentials: true,
            });
            setItems((prev) => prev.filter((it) => it.id !== id));
            toast.success("Demande supprimée.");
        } catch {
            toast.error("Suppression impossible.");
        } finally {
            setDeletingId(null);
        }
    };

    if (user?.must_change_password) {
        return (
            <main data-testid="admin-force-password" className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F]">
                <header className="border-b border-slate-200 bg-white/60 backdrop-blur-xl">
                    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
                        <div>
                            <div className="overline text-[#64748B]">Espace admin</div>
                            <div className="font-serif text-2xl md:text-3xl">Meuble Etterbeek</div>
                        </div>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors inline-flex items-center gap-2"
                        >
                            <LogOut size={16} strokeWidth={1.5} />
                            Déconnexion
                        </button>
                    </div>
                </header>
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
                    <ChangePassword forced />
                </div>
            </main>
        );
    }

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-[#FDFBF7] text-[#1E3A5F]">
            {/* Admin header (separate from public navbar) */}
            <header className="border-b border-slate-200 bg-white/60 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
                    <div>
                        <div className="overline text-[#64748B]">Espace admin</div>
                        <div className="font-serif text-2xl md:text-3xl">Meuble Etterbeek</div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="hidden md:block text-right">
                            <div className="text-sm font-medium">{user?.name}</div>
                            <div className="text-xs text-[#64748B]">{user?.email}</div>
                        </div>
                        <button
                            onClick={logout}
                            data-testid="admin-logout"
                            className="text-sm px-5 py-3 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors inline-flex items-center gap-2"
                        >
                            <LogOut size={16} strokeWidth={1.5} />
                            Déconnexion
                        </button>
                    </div>
                </div>
                {/* Tabs */}
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex gap-2 overflow-x-auto">
                    <button
                        onClick={() => setTab("requests")}
                        data-testid="admin-tab-requests"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "requests" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <Inbox size={16} strokeWidth={1.5} /> Demandes
                    </button>
                    <button
                        onClick={() => setTab("cms")}
                        data-testid="admin-tab-cms"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "cms" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <FileText size={16} strokeWidth={1.5} /> Contenu
                    </button>
                    <button
                        onClick={() => setTab("pages")}
                        data-testid="admin-tab-pages"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "pages" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <Files size={16} strokeWidth={1.5} /> Pages
                    </button>
                    <button
                        onClick={() => setTab("gallery")}
                        data-testid="admin-tab-gallery"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "gallery" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <LayoutGrid size={16} strokeWidth={1.5} /> Réalisations
                    </button>
                    <button
                        onClick={() => setTab("media")}
                        data-testid="admin-tab-media"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "media" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <ImageIcon size={16} strokeWidth={1.5} /> Médias
                    </button>
                    <button
                        onClick={() => setTab("settings")}
                        data-testid="admin-tab-settings"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "settings" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <Settings size={16} strokeWidth={1.5} /> Paramètres
                    </button>
                    <button
                        onClick={() => setTab("security")}
                        data-testid="admin-tab-security"
                        className={`text-sm px-5 py-3 border-b-2 transition-colors inline-flex items-center gap-2 whitespace-nowrap ${
                            tab === "security" ? "border-[#1E3A5F] text-[#1E3A5F]" : "border-transparent text-[#64748B] hover:text-[#1E3A5F]"
                        }`}
                    >
                        <ShieldCheck size={16} strokeWidth={1.5} /> Sécurité
                    </button>
                </div>
            </header>

            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
                {tab === "cms" ? (
                    <CMSEditor />
                ) : tab === "pages" ? (
                    <PagesAdmin />
                ) : tab === "gallery" ? (
                    <GalleryAdmin />
                ) : tab === "media" ? (
                    <MediaLibrary />
                ) : tab === "settings" ? (
                    <SettingsAdmin />
                ) : tab === "security" ? (
                    <ChangePassword />
                ) : (
                <>
                <div className="mb-8 flex items-center justify-between">
                    <div className="font-serif text-xl">Demandes de devis</div>
                    <button
                        onClick={load}
                        data-testid="admin-refresh"
                        className="p-3 border border-[#1E3A5F]/25 hover:bg-[#1E3A5F] hover:text-white transition-colors"
                        aria-label="Rafraîchir"
                    >
                        <RefreshCw size={16} strokeWidth={1.5} />
                    </button>
                </div>

                <div className="mb-10 grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="border border-slate-200 p-6">
                        <div className="overline">Total</div>
                        <div className="font-serif text-4xl mt-2">{items.length}</div>
                    </div>
                    <div className="border border-slate-200 p-6">
                        <div className="overline">Cuisine</div>
                        <div className="font-serif text-4xl mt-2">
                            {items.filter((i) => (i.project_type || "").toLowerCase().includes("cuisine")).length}
                        </div>
                    </div>
                    <div className="border border-slate-200 p-6">
                        <div className="overline">Dressing</div>
                        <div className="font-serif text-4xl mt-2">
                            {items.filter((i) => (i.project_type || "").toLowerCase().includes("dressing")).length}
                        </div>
                    </div>
                    <div className="border border-slate-200 p-6">
                        <div className="overline">Mobilier</div>
                        <div className="font-serif text-4xl mt-2">
                            {items.filter((i) => (i.project_type || "").toLowerCase().includes("mobilier")).length}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-[#64748B]">
                        <Loader2 size={16} className="animate-spin" />
                        Chargement…
                    </div>
                ) : items.length === 0 ? (
                    <div className="border border-dashed border-slate-300 p-16 text-center text-[#64748B]">
                        Aucune demande pour l'instant.
                    </div>
                ) : (
                    <div className="space-y-4">
                        {items.map((it, i) => (
                            <motion.article
                                key={it.id}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: (i % 10) * 0.03 }}
                                data-testid={`admin-quote-${it.id}`}
                                className="border border-slate-200 bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6"
                            >
                                <div className="md:col-span-3">
                                    <div className="font-serif text-xl">{it.name}</div>
                                    <div className="text-xs text-[#64748B] mt-1">
                                        {it.created_at ? new Date(it.created_at).toLocaleString("fr-BE") : ""}
                                    </div>
                                    {it.project_type && (
                                        <div className="mt-3 inline-block text-xs uppercase tracking-[0.18em] px-3 py-1.5 border border-[#1E3A5F]/25">
                                            {it.project_type}
                                        </div>
                                    )}
                                </div>
                                <div className="md:col-span-3 flex flex-col gap-2 text-sm">
                                    <a href={`mailto:${it.email}`} className="flex items-center gap-2 link-underline">
                                        <Mail size={14} strokeWidth={1.5} />
                                        {it.email}
                                    </a>
                                    {it.phone && (
                                        <a href={`tel:${it.phone}`} className="flex items-center gap-2 link-underline">
                                            <Phone size={14} strokeWidth={1.5} />
                                            {it.phone}
                                        </a>
                                    )}
                                </div>
                                <div className="md:col-span-5 text-sm text-[#334155] leading-relaxed whitespace-pre-wrap">
                                    {it.message}
                                </div>
                                <div className="md:col-span-1 flex md:justify-end items-start">
                                    <button
                                        onClick={() => remove(it.id)}
                                        disabled={deletingId === it.id}
                                        data-testid={`admin-delete-${it.id}`}
                                        className="p-3 text-[#64748B] hover:text-[#DC2626] hover:bg-red-50 transition-colors"
                                        aria-label="Supprimer"
                                    >
                                        {deletingId === it.id ? (
                                            <Loader2 size={16} className="animate-spin" />
                                        ) : (
                                            <Trash2 size={16} strokeWidth={1.5} />
                                        )}
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
                </>
                )}
            </div>
        </main>
    );
}
